import { useState, useRef, useCallback, useEffect } from 'react';

// Configuration
const MATTERPORT_SDK_KEY = process.env.REACT_APP_MATTERPORT_SDK_KEY || '740nhc0krt0s6h6ztpx08xgac';
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth token
const getAuthToken = () => {
  try {
    const token = localStorage.getItem('zentara_token');
    if (token) {
      return token;
    }
  } catch (e) {
    console.warn('Could not get auth token');
  }
  return null;
};

// Helper to get user-specific settings key
const getSettingsKey = () => {
  try {
    const userStr = localStorage.getItem('zentara_user');
    if (userStr) {
      const user = JSON.parse(userStr);
      if (user?.id) {
        const key = `matterport-settings-${user.id}`;
        return key;
      }
    }
  } catch (e) {
    console.warn('Could not get user for settings key');
  }
  return 'matterport-settings';
};

// Helper to get saved settings
const getSavedSettings = () => {
  try {
    // Try user-specific key first
    const userKey = getSettingsKey();
    let saved = localStorage.getItem(userKey);
    
    // Fallback to global key if user-specific not found
    if (!saved && userKey !== 'matterport-settings') {
      saved = localStorage.getItem('matterport-settings');
    }
    
    if (saved) {
      const parsed = JSON.parse(saved);
      console.log('Loaded settings from localStorage:', userKey, parsed?.tour);
      return parsed;
    }
  } catch (e) {
    console.warn('Could not parse saved settings:', e);
  }
  console.log('No saved settings found, using defaults');
  return null;
};

/**
 * Custom hook for Matterport SDK integration
 * Uses the import map approach from Matterport docs
 */
export function useMatterport(modelId, tourId, options = {}) {
  const { hideEditorTools = false } = options;
  
  const [sdkInstance, setSdkInstance] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [modelData, setModelData] = useState(null);
  const [cameraPosition, setCameraPosition] = useState(null);
  const [sweeps, setSweeps] = useState([]);
  const [tags, setTags] = useState([]);
  const [persistedTags, setPersistedTags] = useState([]); // Tags from database
  const [hiddenTagIds, setHiddenTagIds] = useState([]); // IDs of tags to hide (for persistent deletion)
  const [openTagId, setOpenTagId] = useState(null); // Currently open tag popup
  const [currentSweep, setCurrentSweep] = useState(null);
  const [customTags, setCustomTags] = useState([]);
  const [quickNavPoints, setQuickNavPoints] = useState([]);
  const [pointerIntersection, setPointerIntersection] = useState(null);
  const [lockedIntersection, setLockedIntersection] = useState(null); // Locked position for tag placement
  const [isTagPlacementMode, setIsTagPlacementMode] = useState(false);
  const [pendingTagData, setPendingTagData] = useState(null);
  const [matterportTourStops, setMatterportTourStops] = useState([]); // Built-in tour stops
  const [matterportLabels, setMatterportLabels] = useState([]); // Built-in labels
  
  const iframeRef = useRef(null);
  const subscriptionsRef = useRef([]);
  const tagPlacementCallbackRef = useRef(null);
  const isTagPlacementModeRef = useRef(false);
  const pendingTagDataRef = useRef(null);
  const pointerIntersectionRef = useRef(null);
  const currentTourIdRef = useRef(tourId);
  const hideEditorToolsRef = useRef(hideEditorTools);

  // Update refs when values change
  useEffect(() => {
    currentTourIdRef.current = tourId;
  }, [tourId]);
  
  useEffect(() => {
    hideEditorToolsRef.current = hideEditorTools;
  }, [hideEditorTools]);

  // Reset state when modelId changes
  useEffect(() => {
    console.log('Model ID changed to:', modelId);
    // Clean up subscriptions from previous model
    subscriptionsRef.current.forEach(sub => {
      if (sub && typeof sub.cancel === 'function') {
        sub.cancel();
      }
    });
    subscriptionsRef.current = [];
    
    // Reset all state
    setSdkInstance(null);
    setConnectionStatus('idle');
    setError(null);
    setModelData(null);
    setCameraPosition(null);
    setSweeps([]);
    setTags([]);
    setCurrentSweep(null);
    setCustomTags([]);
    setQuickNavPoints([]);
    setMatterportTourStops([]);
    setMatterportLabels([]);
  }, [modelId]);

  // Fetch navpoints from API when tourId changes
  useEffect(() => {
    const fetchNavPoints = async () => {
      if (!tourId) return;
      
      const token = getAuthToken();
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/navpoints/tour/${tourId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const navpoints = await res.json();
          console.log('Loaded navpoints from API:', navpoints.length);
          // Transform to quickNavPoints format
          const points = navpoints.map(np => ({
            id: np.id,
            name: np.label,
            sweepId: np.sweep_id,
            position: np.sweep_id ? { sweepId: np.sweep_id } : null,
            rotation: np.rotation,
            icon: np.icon,
            description: np.description,
          }));
          setQuickNavPoints(points);
        }
      } catch (err) {
        console.error('Failed to fetch navpoints:', err);
      }
    };
    
    fetchNavPoints();
  }, [tourId]);

  // Generate iframe URL with options to hide/show native UI
  const getIframeSrc = useCallback((options = {}) => {
    if (!modelId) return '';
    
    // Default: hide most native Matterport UI for a cleaner look
    const {
      showHelp = false,        // Help button
      showBranding = false,    // Matterport branding
      showTitle = false,       // Title bar
      showFloors = true,       // Floor selector (keep for navigation)
      showHighlightReel = false, // Highlight reel
      showGuidedTour = false,  // Guided tour button
      showMattertags = true,   // Keep mattertags visible
      autoPlay = true,         // Auto-start
      quickStart = true,       // Skip intro
      hideEditorTools = false, // Hide edit/admin tools (for training/viewer mode)
    } = options;

    const params = new URLSearchParams({
      m: modelId,
      // Launching
      play: autoPlay ? '1' : '0',
      qs: quickStart ? '1' : '0',
      help: showHelp ? '1' : '0',
      
      // Visibility - hide most UI for cleaner embedded experience
      brand: showBranding ? '1' : '0',
      title: showTitle ? '1' : '0',
      mls: '2',                    // MLS mode - removes branding, about panel
      mt: showMattertags ? '1' : '0',
      hr: showHighlightReel ? '1' : '0',
      hl: '0',                     // Hide highlight reel
      gt: showGuidedTour ? '1' : '0',
      tour: '0',                   // No tour controls or highlight reel
      dh: '0',                     // Hide dollhouse button
      measurements: '0',           // Hide measurement/ruler icon
      views: '0',                  // Hide Views
      pin: '0',                    // Hide 360 view pins
      portal: '0',                 // Hide connection portals
      tagNav: '0',                 // Hide tag navigation buttons
      
      // User Interface
      f: showFloors ? '1' : '0',
      fp: '0',                     // Hide floor plan button
      search: '0',                 // Hide search icon
      vr: '0',                     // Hide VR button
      
      // Other
      lang: 'en',
      applicationKey: MATTERPORT_SDK_KEY,
    });
    
    // Additional params for training tours - hide everything possible
    if (hideEditorTools) {
      params.set('nozoom', '0');   // Keep zoom enabled
      params.set('wh', '1');       // Enable scroll wheel for zoom
    }
    
    return `https://my.matterport.com/show?${params.toString()}`;
  }, [modelId]);

  // Connect to SDK using global window.MP_SDK
  const connect = useCallback(async (iframe) => {
    if (!iframe || !modelId) {
      setError('Missing iframe element or model ID');
      setConnectionStatus('error');
      return null;
    }

    iframeRef.current = iframe;
    setConnectionStatus('connecting');
    setError(null);

    try {
      // Wait for iframe to load
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Check if SDK is loaded globally
      if (!window.MP_SDK) {
        console.log('Waiting for MP_SDK to load...');
        // Wait for script to load
        await new Promise((resolve, reject) => {
          const checkInterval = setInterval(() => {
            if (window.MP_SDK) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 500);
          setTimeout(() => {
            clearInterval(checkInterval);
            reject(new Error('SDK load timeout'));
          }, 10000);
        });
      }
      
      console.log('MP_SDK found on window:', window.MP_SDK);
      
      // Connect to the showcase iframe with the SDK key
      console.log('Connecting to showcase...');
      const mpSdk = await window.MP_SDK.connect(iframe, MATTERPORT_SDK_KEY, '3.10');
      
      console.log('✅ SDK Connected successfully!');
      setSdkInstance(mpSdk);
      setConnectionStatus('connected');

      // Try to customize sweep indicator colors (Zentara branding: Gold #C5A24A)
      try {
        if (mpSdk.Settings && mpSdk.Settings.update) {
          // Try to set sweep indicator color to Zentara gold
          await mpSdk.Settings.update('sweeps', {
            'color': { r: 0.77, g: 0.64, b: 0.29 },  // #C5A24A converted to RGB (0-1)
          });
          console.log('Sweep colors updated to Zentara gold');
        }
      } catch (e) {
        console.log('Sweep color customization not available:', e.message);
      }

      // Hide editor tools for training tours
      if (hideEditorToolsRef.current) {
        console.log('Hiding editor tools for training tour...');
        try {
          // Try to disable edit mode / workshop mode
          if (mpSdk.Mode && mpSdk.Mode.Current) {
            const currentMode = await mpSdk.Mode.Current.get();
            console.log('Current mode:', currentMode);
            // Try to set to viewer mode if in workshop/edit mode
            if (mpSdk.Mode.TransitionTo) {
              await mpSdk.Mode.TransitionTo(mpSdk.Mode.Mode.INSIDE);
            }
          }
        } catch (e) {
          console.log('Mode control not available:', e.message);
        }
        
        try {
          // Try to hide UI components using Settings
          if (mpSdk.Settings && mpSdk.Settings.update) {
            await mpSdk.Settings.update('features', {
              'measurements': false,
              'mattertags': false,  // Hide mattertag editing (not display)
              'tours': false,       // Hide tour editor
              'media': false,       // Hide media tools
            });
            console.log('Editor features hidden via Settings');
          }
        } catch (e) {
          console.log('Feature hiding not available:', e.message);
        }
      }

      // Try to hide native Matterport UI elements
      try {
        // Hide default UI components using Settings
        if (mpSdk.Settings && mpSdk.Settings.update) {
          await mpSdk.Settings.update('ui', {
            'Share': false,
            'About': false,
            'Logo': false,
            'VR': false,
            'Help': false,
            'MeasureTool': false,
            'Highlight': false,
            'Contact': false,
          });
          console.log('UI settings updated via Settings.update');
        }
      } catch (e) {
        console.log('Settings.update not available:', e.message);
      }

      // Alternative: Try App.state to hide UI
      try {
        if (mpSdk.App && mpSdk.App.state) {
          const appState = await mpSdk.App.state.subscribe((state) => {
            // Check for UI state options
          });
        }
      } catch (e) {
        // App.state might not be available
      }

      // Get model data with sweeps
      try {
        const data = await mpSdk.Model.getData();
        setModelData(data);
        console.log('Model:', data.sid);
        console.log('Raw sweeps data:', data.sweeps);
        
        if (data.sweeps) {
          let sweepArray;
          
          // Handle both array and object formats
          if (Array.isArray(data.sweeps)) {
            // Sweeps is an array of sweep objects
            sweepArray = data.sweeps.map((sweep, index) => ({
              id: sweep.sid || sweep.uuid || sweep.id || `sweep-${index}`,
              index: index + 1,
              position: sweep.position,
              rotation: sweep.rotation,
              floor: sweep.floor,
              enabled: sweep.enabled !== false,
            }));
          } else {
            // Sweeps is an object keyed by sweep ID
            sweepArray = Object.entries(data.sweeps).map(([sid, sweepData], index) => ({
              id: sid,
              index: index + 1,
              position: sweepData.position,
              rotation: sweepData.rotation,
              floor: sweepData.floor,
              enabled: true,
            }));
          }
          
          setSweeps(sweepArray);
          console.log(`Found ${sweepArray.length} sweeps:`, sweepArray.slice(0, 3));
        }
      } catch (e) {
        console.warn('Could not get model data:', e);
      }

      // Subscribe to current sweep
      try {
        const sweepSub = mpSdk.Sweep.current.subscribe((sweepData) => {
          if (sweepData && sweepData.sid) {
            setCurrentSweep({ id: sweepData.sid, ...sweepData });
          }
        });
        subscriptionsRef.current.push(sweepSub);
      } catch (e) {
        console.warn('Could not subscribe to sweep:', e);
      }

      // Also listen for sweep.enter events as fallback
      try {
        mpSdk.on('sweep.enter', (oldSweep, newSweep) => {
          const sweepId = typeof newSweep === 'string' ? newSweep : newSweep?.sid || newSweep?.id;
          if (sweepId) {
            setCurrentSweep({ id: sweepId });
          }
        });
      } catch (e) {
        // sweep.enter event not available - fallback silently
      }

      // Subscribe to camera
      try {
        const cameraSub = mpSdk.Camera.pose.subscribe((pose) => {
          setCameraPosition({
            position: pose.position,
            rotation: pose.rotation,
            mode: pose.mode,
            zoom: pose.zoom, // Include zoom level if available
          });
        });
        subscriptionsRef.current.push(cameraSub);
      } catch (e) {
        console.warn('Could not subscribe to camera:', e);
      }

      // Subscribe to pointer intersection for click-to-place tags
      try {
        const pointerSub = mpSdk.Pointer.intersection.subscribe(intersection => {
          if (intersection && intersection.position) {
            const intersectionData = {
              position: intersection.position,
              normal: intersection.normal,
              object: intersection.object,
            };
            setPointerIntersection(intersectionData);
            pointerIntersectionRef.current = intersectionData;
          }
        });
        subscriptionsRef.current.push(pointerSub);
        console.log('Pointer intersection subscription active');
      } catch (e) {
        console.warn('Could not subscribe to pointer intersection:', e);
      }

      // Subscribe to tag selection (track when tag popups are opened)
      try {
        if (mpSdk.Tag && mpSdk.Tag.selected) {
          const tagSelectedSub = mpSdk.Tag.selected.subscribe((selectedTags) => {
            // selectedTags is a Set of currently open tag IDs
            if (selectedTags && selectedTags.size > 0) {
              const openId = Array.from(selectedTags)[0];
              setOpenTagId(openId);
              console.log('Tag opened:', openId);
            } else {
              setOpenTagId(null);
              console.log('All tags closed');
            }
          });
          subscriptionsRef.current.push(tagSelectedSub);
          console.log('Tag selection subscription active');
        }
      } catch (e) {
        console.warn('Could not subscribe to tag selection:', e);
      }

      // Get tags using new Tag API (replaces deprecated Mattertag API)
      // First, fetch hidden tags from database
      let hiddenTags = [];
      const tourId = currentTourIdRef.current;
      if (tourId) {
        try {
          const token = getAuthToken();
          if (token) {
            const hiddenRes = await fetch(`${API_URL}/api/tags/hidden/${tourId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (hiddenRes.ok) {
              hiddenTags = await hiddenRes.json();
              setHiddenTagIds(hiddenTags);
              console.log('Loaded hidden tags from database:', hiddenTags);
            }
          }
        } catch (e) {
          console.warn('Could not fetch hidden tags:', e);
        }
      }
      
      try {
        // Use Tag.data observable to get existing tags
        const tagsCollection = [];
        const tagDataPromise = new Promise((resolve) => {
          let resolved = false;
          let addedCount = 0;
          
          const subscription = mpSdk.Tag.data.subscribe({
            onAdded: (index, tag, collection) => {
              addedCount++;
              // Log to see what properties are available
              console.log('Tag.data onAdded:', JSON.stringify({
                sid: tag.sid,
                id: tag.id,
                label: tag.label,
                keys: Object.keys(tag)
              }));
              
              // Try multiple ID properties (different SDK versions may use different names)
              const tagId = tag.sid || tag.id || tag.tagId;
              
              // Skip hidden tags
              if (tagId && !hiddenTags.includes(tagId)) {
                tagsCollection.push({
                  id: tagId,
                  label: tag.label || 'Unnamed Tag',
                  description: tag.description || '',
                  position: tag.anchorPosition,
                  stemVector: tag.stemVector,
                  color: tag.color,
                  navigationType: 'tag',
                });
              } else if (hiddenTags.includes(tagId)) {
                console.log('Filtering out hidden tag:', tagId);
              }
            },
            onCollectionUpdated: (collection) => {
              // Collection is fully loaded - wait a bit to ensure all onAdded calls complete
              console.log('Tag collection updated, size:', collection.length, 'added so far:', addedCount);
              if (!resolved) {
                setTimeout(() => {
                  if (!resolved) {
                    resolved = true;
                    subscription.cancel();
                    console.log('Resolving with tags:', tagsCollection.length);
                    resolve(tagsCollection);
                  }
                }, 1000); // Wait 1 second for all tags to be processed
              }
            }
          });
          
          // Timeout fallback - increased to 5 seconds
          setTimeout(() => {
            if (!resolved) {
              resolved = true;
              subscription.cancel();
              console.log('Tag subscription timed out, resolving with:', tagsCollection.length, 'tags');
              resolve(tagsCollection);
            }
          }, 5000);
        });
        
        const processedTags = await tagDataPromise;
        setTags(processedTags);
        console.log(`Found ${processedTags.length} visible tags via Tag API (after filtering hidden):`, processedTags.map(t => ({ label: t.label, id: t.id })));
      } catch (e) {
        console.warn('Could not get tags via Tag API, trying legacy Mattertag:', e);
        // Fallback to legacy Mattertag API if Tag API not available
        try {
          const tagData = await mpSdk.Mattertag.getData();
          // Filter out hidden tags
          const processedTags = tagData
            .filter(tag => !hiddenTags.includes(tag.sid))
            .map(tag => ({
              id: tag.sid,
              label: tag.label,
              description: tag.description,
              position: tag.anchorPosition,
              color: tag.color,
              navigationType: 'mattertag',
            }));
          setTags(processedTags);
          console.log(`Found ${processedTags.length} visible tags via legacy Mattertag API (after filtering hidden)`);
        } catch (e2) {
          console.warn('Could not get tags:', e2);
        }
      }

      // Load persisted tags from database and recreate them using NEW Tag API
      // tourId is already defined above when fetching hidden tags
      if (tourId) {
        try {
          const token = getAuthToken();
          if (token) {
            const response = await fetch(`${API_URL}/api/tags?tour_id=${tourId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
              const savedTags = await response.json();
              console.log(`Found ${savedTags.length} persisted tags in database`);
              setPersistedTags(savedTags);
              
              // Get current tags from SDK to check for duplicates (not from state)
              const existingTagLabels = new Set();
              await new Promise((resolve) => {
                let resolved = false;
                const sub = mpSdk.Tag.data.subscribe({
                  onAdded: (index, tag) => {
                    existingTagLabels.add(tag.label);
                  },
                  onCollectionUpdated: () => {
                    if (!resolved) { resolved = true; setTimeout(() => { sub.cancel(); resolve(); }, 300); }
                  }
                });
                setTimeout(() => { if (!resolved) { resolved = true; sub.cancel(); resolve(); } }, 1500);
              });
              
              // Recreate each persisted tag using the NEW Tag.add API
              for (const savedTag of savedTags) {
                try {
                  const alreadyExists = existingTagLabels.has(savedTag.label);
                  
                  if (!alreadyExists) {
                    // Ensure position values are valid numbers
                    const anchorPos = {
                      x: Number(savedTag.position?.x) || 0,
                      y: Number(savedTag.position?.y) || 0,
                      z: Number(savedTag.position?.z) || 0,
                    };
                    const stemVec = {
                      x: Number(savedTag.stem_vector?.x) || 0,
                      y: Number(savedTag.stem_vector?.y) || 0.15,
                      z: Number(savedTag.stem_vector?.z) || 0,
                    };
                    
                    // Use the NEW mpSdk.Tag.add API (single object, not array)
                    const [newTagId] = await mpSdk.Tag.add({
                      label: savedTag.label,
                      description: savedTag.description || '',
                      anchorPosition: anchorPos,
                      stemVector: stemVec,
                      color: savedTag.color || { r: 0.8, g: 0.6, b: 0.2 },
                    });
                    console.log(`Recreated persisted tag with NEW Tag API: ${savedTag.label} -> ${newTagId}`);
                    existingTagLabels.add(savedTag.label);
                    
                    // Update the Matterport ID in database if different
                    // This is critical - every time we recreate, the SDK gives a new ID
                    if (newTagId && savedTag.matterport_tag_id !== newTagId) {
                      console.log(`Updating matterport_tag_id for ${savedTag.label}: ${savedTag.matterport_tag_id} -> ${newTagId}`);
                      try {
                        const updateRes = await fetch(`${API_URL}/api/tags/${savedTag.id}/matterport-id?matterport_tag_id=${newTagId}`, {
                          method: 'PATCH',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (updateRes.ok) {
                          // Update local savedTags array so matching works correctly
                          savedTag.matterport_tag_id = newTagId;
                          console.log(`Successfully updated matterport_tag_id in database`);
                        } else {
                          console.warn(`Failed to update matterport_tag_id:`, await updateRes.text());
                        }
                      } catch (updateErr) {
                        console.warn(`Error updating matterport_tag_id:`, updateErr);
                      }
                    }
                  }
                } catch (tagError) {
                  console.warn(`Could not recreate tag ${savedTag.label}:`, tagError);
                }
              }
              
              // Refresh tags list after recreation using Tag.data observable
              // Use a longer delay and better logging
              const refreshedTags = [];
              await new Promise((resolve) => {
                let resolved = false;
                const sub = mpSdk.Tag.data.subscribe({
                  onAdded: (index, tag) => {
                    // Log full tag object to see available properties
                    console.log('Tag.data onAdded - full tag object:', JSON.stringify({
                      sid: tag.sid,
                      id: tag.id,
                      label: tag.label,
                      keys: Object.keys(tag)
                    }));
                    
                    // Try multiple ID properties
                    const tagId = tag.sid || tag.id || tag.tagId;
                    
                    // Filter out hidden tags
                    if (tagId && !hiddenTags.includes(tagId)) {
                      // Try to find this tag in our database:
                      // 1. First by matterport_tag_id (most reliable after recreation)
                      // 2. Then by label as fallback
                      let dbTag = savedTags.find(st => st.matterport_tag_id === tagId);
                      if (!dbTag) {
                        dbTag = savedTags.find(st => st.label === tag.label);
                      }
                      
                      refreshedTags.push({
                        id: tagId,
                        dbId: dbTag?.id || null, // Our database ID for deletion
                        label: tag.label,
                        description: tag.description,
                        position: tag.anchorPosition,
                        stemVector: tag.stemVector,
                        color: tag.color,
                        navigationType: 'tag',
                        isFromDatabase: !!dbTag, // Flag to know if this is our tag
                      });
                      
                      if (dbTag) {
                        console.log(`Matched SDK tag ${tagId} to DB tag ${dbTag.id} (${dbTag.label})`);
                      }
                    }
                  },
                  onCollectionUpdated: () => {
                    if (!resolved) {
                      resolved = true;
                      setTimeout(() => { sub.cancel(); resolve(); }, 500);
                    }
                  }
                });
                setTimeout(() => { if (!resolved) { resolved = true; sub.cancel(); resolve(); } }, 3000);
              });
              console.log('Refreshed tags after recreation:', refreshedTags.map(t => ({ id: t.id, label: t.label })));
              setTags(refreshedTags);
            }
          }
        } catch (e) {
          console.warn('Could not load persisted tags:', e);
        }
      }

      // Get Matterport Tour data (snapshots/highlights)
      try {
        if (mpSdk.Tour) {
          const tourData = await mpSdk.Tour.getData();
          console.log('Raw Tour data:', JSON.stringify(tourData, null, 2));
          
          // Tour snapshots have different structure
          if (tourData && tourData.snapshots && Array.isArray(tourData.snapshots)) {
            const tourStops = tourData.snapshots.map((snapshot, index) => ({
              id: snapshot.sid || `tour-stop-${index}`,
              name: snapshot.name || `Stop ${index + 1}`,
              sweepId: snapshot.sweepUUID || snapshot.sweep,
              mode: snapshot.mode,
              position: snapshot.position,
              rotation: snapshot.rotation,
              index: index,
              navigationType: 'tour',
            }));
            console.log('Tour stops processed:', tourStops.map(s => ({ name: s.name, sweepId: s.sweepId })));
            setMatterportTourStops(tourStops);
          } else if (tourData && Array.isArray(tourData)) {
            // Alternative format - direct array
            const tourStops = tourData.map((stop, index) => ({
              id: stop.sid || stop.id || `tour-stop-${index}`,
              name: stop.name || `Stop ${index + 1}`,
              sweepId: stop.sweepUUID || stop.sweep || stop.sweepId,
              mode: stop.mode,
              position: stop.position,
              rotation: stop.rotation,
              index: index,
              navigationType: 'tour',
            }));
            console.log('Tour stops (array format):', tourStops.map(s => ({ name: s.name, sweepId: s.sweepId })));
            setMatterportTourStops(tourStops);
          }
        }
      } catch (e) {
        console.warn('Could not get tour data:', e);
      }

      // Get Sweep data with room names if available
      try {
        // Some sweeps have room info
        const sweepData = await mpSdk.Sweep.data.filter((sweep) => sweep.enabled).catch(() => []);
        if (sweepData && sweepData.length > 0) {
          console.log('Sweep sample:', JSON.stringify(sweepData[0], null, 2));
        }
      } catch (e) {
        console.warn('Could not get detailed sweep data:', e);
      }

      // Get Room data if available
      try {
        if (mpSdk.Room) {
          const roomData = await mpSdk.Room.getData();
          console.log('Room data:', roomData);
        }
      } catch (e) {
        console.warn('Could not get room data:', e);
      }

      // Get Labels data (room labels, area names) using new observable API
      try {
        if (mpSdk.Label && mpSdk.Label.data) {
          const labelsCollection = [];
          await new Promise((resolve) => {
            let resolved = false;
            const subscription = mpSdk.Label.data.subscribe({
              onAdded: (index, label, collection) => {
                console.log('Label.data onAdded:', JSON.stringify({
                  sid: label.sid,
                  text: label.text,
                  floor: label.floor,
                  position: label.position,
                  visible: label.visible,
                }));
                
                labelsCollection.push({
                  id: label.sid,
                  label: label.text,
                  text: label.text,
                  position: label.position,
                  floor: label.floor,
                  floorInfo: label.floorInfo,
                  visible: label.visible,
                  navigationType: 'label',
                });
              },
              onCollectionUpdated: (collection) => {
                if (!resolved) {
                  resolved = true;
                  setTimeout(() => {
                    subscription.cancel();
                    resolve();
                  }, 500);
                }
              }
            });
            // Timeout fallback
            setTimeout(() => {
              if (!resolved) {
                resolved = true;
                subscription.cancel();
                resolve();
              }
            }, 3000);
          });
          
          console.log(`Found ${labelsCollection.length} labels via Label.data API`);
          setMatterportLabels(labelsCollection);
        } else if (mpSdk.Label) {
          // Fallback to deprecated getData()
          const labelData = await mpSdk.Label.getData();
          console.log('Raw Label data (deprecated):', JSON.stringify(labelData, null, 2));
          
          if (labelData && Array.isArray(labelData)) {
            const labels = labelData.map((label, index) => ({
              id: label.sid || `label-${index}`,
              label: label.text,
              text: label.text,
              position: label.position,
              floor: label.floor,
              floorInfo: label.floorInfo,
              visible: label.visible,
              navigationType: 'label',
            }));
            console.log('Labels found:', labels.map(l => ({ label: l.label })));
            setMatterportLabels(labels);
          }
        }
      } catch (e) {
        console.warn('Could not get label data:', e);
      }

      // Hide native Matterport UI elements using SDK methods
      try {
        console.log('Attempting to hide native Matterport UI...');
        
        // Try Tag.toggleNavControls - hides overhead navigation UI
        if (mpSdk.Tag && mpSdk.Tag.toggleNavControls) {
          await mpSdk.Tag.toggleNavControls(false).catch(e => console.warn('toggleNavControls:', e));
          console.log('Nav controls hidden');
        }
        
        // Try Tag.toggleDocking - hides tag dock buttons
        if (mpSdk.Tag && mpSdk.Tag.toggleDocking) {
          await mpSdk.Tag.toggleDocking(false).catch(e => console.warn('toggleDocking:', e));
          console.log('Tag docking hidden');
        }
        
        // Try to hide labels via Settings
        if (mpSdk.Settings && mpSdk.Settings.update) {
          await mpSdk.Settings.update('labels', false).catch(() => {});
        }
        
        console.log('SDK UI hiding completed');
      } catch (e) {
        console.warn('Could not configure SDK UI:', e);
      }

      return mpSdk;
    } catch (err) {
      console.error('SDK Connection Error:', err);
      // Even if SDK fails, show the model
      setConnectionStatus('connected');
      setModelData({ sid: modelId });
      setError('SDK limited: ' + err.message);
      return null;
    }
  }, [modelId]);

  // Navigate to sweep with optional camera rotation
  // Based on Matterport SDK docs: https://matterport.github.io/showcase-sdk/docs/reference/current/modules/sweep.html
  // Sweep.moveTo supports rotation directly in MoveToOptions - no need for separate Camera.setRotation!
  const navigateToSweep = useCallback(async (sweepId, options = {}) => {
    if (!sdkInstance) {
      console.warn('SDK not connected');
      return;
    }
    try {
      console.log('=== NAVIGATION START ===');
      console.log('Navigating to sweep:', sweepId);
      console.log('Options:', JSON.stringify(options));
      
      // Get transition style from options or user-specific localStorage settings
      let transitionStyle = options.transitionStyle;
      if (!transitionStyle) {
        try {
          const savedSettings = getSavedSettings();
          console.log('Navigation: Loaded settings:', JSON.stringify(savedSettings?.tour));
          if (savedSettings && savedSettings.tour) {
            transitionStyle = savedSettings.tour.transitionStyle || 'fly';
          }
        } catch (e) {
          console.error('Error loading settings:', e);
          transitionStyle = 'fly';
        }
      }
      if (!transitionStyle) {
        transitionStyle = 'fly';
      }
      
      console.log('Navigation: Using transition style:', transitionStyle);
      
      // Map transition style to SDK transition type
      const transitionMap = {
        'fly': sdkInstance.Sweep.Transition.FLY,
        'fade': sdkInstance.Sweep.Transition.FADEOUT,
        'instant': sdkInstance.Sweep.Transition.INSTANT,
      };
      const transition = transitionMap[transitionStyle] || sdkInstance.Sweep.Transition.FLY;
      
      // Get transition speed from settings (in seconds), convert to ms
      // Default: 0.3 seconds for all types now
      const isInstant = transitionStyle === 'instant';
      let transitionTime;
      if (isInstant) {
        transitionTime = 0;
      } else {
        const savedSettings = getSavedSettings();
        const speedInSeconds = savedSettings?.tour?.speed ?? 0.3; // Default 0.3s if not set
        transitionTime = options.transitionTime || Math.round(speedInSeconds * 1000);
      }
      
      console.log('Navigation: Transition time (ms):', transitionTime);
      
      // Build moveTo options - rotation is now passed DIRECTLY to moveTo!
      // This eliminates the two-step process that caused the "snap" effect
      const moveToOptions = {
        transition: transition,
        transitionTime: transitionTime,
      };
      
      // Add rotation directly to moveTo options if specified
      if (options.rotation && (options.rotation.x !== undefined || options.rotation.y !== undefined)) {
        moveToOptions.rotation = {
          x: options.rotation.x || 0,
          y: options.rotation.y || 0
        };
        console.log('Including rotation in moveTo:', moveToOptions.rotation);
        console.log('Full options.rotation received:', JSON.stringify(options.rotation));
      } else {
        console.log('NO ROTATION in options:', options.rotation);
      }
      
      console.log('Calling Sweep.moveTo with options:', JSON.stringify(moveToOptions));
      console.log('moveToOptions.rotation:', moveToOptions.rotation);
      
      // Single call to navigate AND set rotation
      await sdkInstance.Sweep.moveTo(sweepId, moveToOptions);
      console.log('Sweep.moveTo completed');
      
      // IMPORTANT: Explicitly set rotation AFTER moveTo completes
      // Matterport's Sweep.moveTo does NOT reliably apply the rotation parameter
      // Camera.setRotation only works in PANORAMA mode (Inside View)
      if (moveToOptions.rotation) {
        console.log('=== APPLYING ROTATION AFTER NAVIGATION ===');
        console.log('Target rotation:', moveToOptions.rotation);
        
        try {
          // Small wait for sweep to settle (reduced from 1500ms)
          await new Promise(resolve => setTimeout(resolve, 300));
          
          // Use setRotation to set the absolute rotation
          const targetRotation = {
            x: Number(moveToOptions.rotation.x) || 0,
            y: Number(moveToOptions.rotation.y) || 0
          };
          console.log('Calling Camera.setRotation with:', JSON.stringify(targetRotation));
          
          // Call setRotation with faster speed
          await sdkInstance.Camera.setRotation(targetRotation, { speed: 2.5 });
          console.log('Camera.setRotation call completed');
          
        } catch (rotErr) {
          console.error('Camera.setRotation failed:', rotErr);
          console.error('Error details:', rotErr.message, rotErr.stack);
        }
      }
      
      // Apply zoom if specified (this still needs to be separate)
      if (options.zoom && options.zoom !== 1.0) {
        console.log('Setting camera zoom to:', options.zoom);
        try {
          await sdkInstance.Camera.zoomTo(options.zoom);
          console.log('Camera.zoomTo completed successfully');
        } catch (zoomError) {
          console.warn('Camera.zoomTo failed:', zoomError.message);
        }
      }
      
      console.log('=== NAVIGATION END ===');
      
    } catch (e) {
      console.error('Navigation failed:', e);
    }
  }, [sdkInstance]);

  // Navigate with full pose (position + rotation)
  const navigateToPosition = useCallback(async (pose) => {
    if (!sdkInstance) {
      console.warn('SDK not connected');
      return;
    }
    try {
      // Use Camera.moveTo for precise positioning
      await sdkInstance.Camera.moveTo(pose.position, {
        rotation: pose.rotation,
        transition: sdkInstance.Sweep?.Transition?.FLY || 'fly',
        transitionTime: 1500,
      });
    } catch (e) {
      console.error('Position navigation failed:', e);
      // Fallback: try just setting pose
      try {
        await sdkInstance.Camera.setPose(pose);
      } catch (e2) {
        console.error('Pose set failed:', e2);
      }
    }
  }, [sdkInstance]);

  // Get current camera pose for saving
  const getCurrentPose = useCallback(() => {
    return cameraPosition;
  }, [cameraPosition]);

  // Navigate to tag using NEW Tag API
  const navigateToTag = useCallback(async (tagId) => {
    console.log('navigateToTag called with ID:', tagId);
    
    if (!sdkInstance) {
      console.warn('Cannot navigate to tag: SDK not connected');
      return;
    }
    
    if (!tagId) {
      console.warn('Cannot navigate to tag: No tag ID provided');
      return;
    }
    
    try {
      // Use NEW Tag.navigateToTag API
      console.log('Attempting Tag.navigateToTag...');
      const transition = sdkInstance.Tag?.Transition?.FLY || sdkInstance.Mattertag?.Transition?.FLY;
      console.log('Using transition:', transition);
      
      if (sdkInstance.Tag && sdkInstance.Tag.navigateToTag) {
        await sdkInstance.Tag.navigateToTag(tagId, transition);
        console.log('Tag.navigateToTag successful');
      } else {
        throw new Error('Tag.navigateToTag not available');
      }
    } catch (e) {
      console.warn('Tag.navigateToTag failed:', e.message, '- trying legacy Mattertag');
      // Fallback to legacy API
      try {
        const legacyTransition = sdkInstance.Mattertag?.Transition?.FLY;
        await sdkInstance.Mattertag.navigateToTag(tagId, legacyTransition);
        console.log('Mattertag.navigateToTag successful');
      } catch (e2) {
        console.error('All tag navigation methods failed:', e2);
      }
    }
  }, [sdkInstance]);

  // Navigate to a label's position
  const navigateToLabel = useCallback(async (label) => {
    if (!sdkInstance) {
      console.warn('Cannot navigate to label: SDK not connected');
      return;
    }
    
    if (!label || !label.position) {
      console.warn('Cannot navigate to label: No position provided');
      return;
    }
    
    try {
      console.log('Navigating to label:', label.text || label.label, 'at position:', label.position);
      
      // Find the nearest sweep to the label position
      if (sdkInstance.Sweep && sdkInstance.Sweep.data) {
        const sweeps = await new Promise((resolve) => {
          const sweepList = [];
          let resolved = false;
          const sub = sdkInstance.Sweep.data.subscribe({
            onAdded: (index, sweep) => {
              if (sweep.enabled) {
                sweepList.push(sweep);
              }
            },
            onCollectionUpdated: () => {
              if (!resolved) {
                resolved = true;
                setTimeout(() => { sub.cancel(); resolve(sweepList); }, 200);
              }
            }
          });
          setTimeout(() => { if (!resolved) { resolved = true; sub.cancel(); resolve(sweepList); } }, 1500);
        });
        
        // Find nearest sweep to label position
        let nearestSweep = null;
        let nearestDistance = Infinity;
        
        for (const sweep of sweeps) {
          if (sweep.position) {
            const dx = sweep.position.x - label.position.x;
            const dy = sweep.position.y - label.position.y;
            const dz = sweep.position.z - label.position.z;
            const distance = Math.sqrt(dx*dx + dy*dy + dz*dz);
            
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestSweep = sweep;
            }
          }
        }
        
        if (nearestSweep) {
          console.log('Found nearest sweep:', nearestSweep.id, 'at distance:', nearestDistance.toFixed(2));
          await navigateToSweep(nearestSweep.id);
        } else {
          console.warn('No nearby sweep found for label');
        }
      }
    } catch (e) {
      console.error('Label navigation failed:', e);
    }
  }, [sdkInstance, navigateToSweep]);

  // Navigate to a tour stop by index
  const navigateToTourStop = useCallback(async (stopIndex) => {
    if (!sdkInstance) return;
    try {
      console.log('Navigating to tour stop index:', stopIndex);
      // Tour.step moves to a specific snapshot
      if (sdkInstance.Tour && sdkInstance.Tour.step) {
        await sdkInstance.Tour.step(stopIndex);
        console.log('Tour.step completed');
      }
    } catch (e) {
      console.error('Tour stop navigation failed:', e);
    }
  }, [sdkInstance]);

  // Navigate to any model location (tour stop, tag, label, or sweep)
  const navigateToModelLocation = useCallback(async (location) => {
    if (!sdkInstance) return;
    
    console.log('navigateToModelLocation called with:', location);
    
    try {
      if ((location.navigationType === 'tag' || location.navigationType === 'mattertag' || location.type === 'mattertag') && location.id) {
        // Navigate to tag using NEW Tag API
        console.log('Navigating to tag:', location.id);
        try {
          await sdkInstance.Tag.navigateToTag(location.id, sdkInstance.Tag.Transition.FLY);
        } catch (e) {
          // Fallback to legacy Mattertag API
          console.warn('Tag.navigateToTag failed, trying legacy:', e);
          await sdkInstance.Mattertag.navigateToTag(location.id, sdkInstance.Mattertag.Transition.FLY);
        }
      } else if ((location.navigationType === 'tour' || location.type === 'tour') && location.index !== undefined) {
        // Navigate to tour stop by index
        console.log('Navigating to tour stop index:', location.index);
        if (sdkInstance.Tour && sdkInstance.Tour.step) {
          await sdkInstance.Tour.step(location.index);
        }
      } else if (location.navigationType === 'label' || location.type === 'label') {
        // Navigate to label's position (find nearest sweep)
        console.log('Navigating to label:', location.text || location.label);
        await navigateToLabel(location);
      } else if (location.sweepId) {
        // Navigate to sweep
        console.log('Navigating to sweep:', location.sweepId);
        await navigateToSweep(location.sweepId, { rotation: location.rotation });
      } else if (location.position) {
        // Navigate to position
        console.log('Navigating to position:', location.position);
        await navigateToPosition({ position: location.position, rotation: location.rotation });
      } else {
        console.warn('No valid navigation target for location:', location);
      }
    } catch (e) {
      console.error('Model location navigation failed:', e);
    }
  }, [sdkInstance, navigateToSweep, navigateToPosition]);

  // Start tag placement mode - user clicks on model to place tag
  const startTagPlacement = useCallback((tagData, onPlaced) => {
    console.log('Starting tag placement mode with data:', tagData);
    // Set both state and refs (refs needed for click handler closure)
    setIsTagPlacementMode(true);
    setPendingTagData(tagData);
    setLockedIntersection(null); // Clear any previously locked position
    isTagPlacementModeRef.current = true;
    pendingTagDataRef.current = tagData;
    tagPlacementCallbackRef.current = onPlaced;
  }, []);

  // Lock the current intersection position for tag placement (or clear with null)
  const lockTagPosition = useCallback((position = undefined) => {
    if (position === null) {
      // Clear locked position
      setLockedIntersection(null);
    } else if (pointerIntersection) {
      console.log('Locking position for tag:', pointerIntersection);
      setLockedIntersection({ ...pointerIntersection });
    }
  }, [pointerIntersection]);

  // Cancel tag placement mode
  const cancelTagPlacement = useCallback(() => {
    setIsTagPlacementMode(false);
    setPendingTagData(null);
    setLockedIntersection(null);
    isTagPlacementModeRef.current = false;
    pendingTagDataRef.current = null;
    tagPlacementCallbackRef.current = null;
  }, []);

  // Place tag at LOCKED intersection using NEW Tag API (not deprecated Mattertag)
  const placeTagAtIntersection = useCallback(async () => {
    // ONLY use locked position - this ensures the tag is placed exactly where user clicked "Lock"
    if (!lockedIntersection) {
      console.warn('Cannot place tag: no locked position. Please lock a position first.');
      return null;
    }
    
    if (!sdkInstance || !pendingTagData) {
      console.warn('Cannot place tag: missing SDK or tag data', {
        hasSDK: !!sdkInstance,
        hasPendingData: !!pendingTagData
      });
      return null;
    }

    try {
      console.log('=== PLACING TAG WITH NEW Tag API ===');
      console.log('Locked intersection:', JSON.stringify(lockedIntersection, null, 2));
      
      // The anchorPosition is where the tag's base attaches to the mesh
      // Ensure values are proper numbers (not strings or undefined)
      const anchorPosition = {
        x: Number(lockedIntersection.position.x) || 0,
        y: Number(lockedIntersection.position.y) || 0,
        z: Number(lockedIntersection.position.z) || 0,
      };
      
      // Validate that we have valid numbers
      if (isNaN(anchorPosition.x) || isNaN(anchorPosition.y) || isNaN(anchorPosition.z)) {
        console.error('Invalid anchor position - contains NaN values:', anchorPosition);
        throw new Error('Invalid anchor position');
      }
      
      // Calculate stem vector from surface normal (perpendicular to surface)
      // The stem points from the anchor toward the viewer
      // Use the surface normal to determine the direction
      const stemLength = 0.2; // Length of the tag stem in meters
      let stemVector;
      
      if (lockedIntersection.normal) {
        const nx = Number(lockedIntersection.normal.x) || 0;
        const ny = Number(lockedIntersection.normal.y) || 0;
        const nz = Number(lockedIntersection.normal.z) || 0;
        
        // Use the surface normal to orient the stem perpendicular to the surface
        stemVector = {
          x: nx * stemLength,
          y: Math.abs(ny) < 0.5 
             ? stemLength // If surface is mostly vertical, stem goes up
             : ny * stemLength,
          z: nz * stemLength,
        };
        
        // Ensure minimum stem height so tag is visible
        if (stemVector.y < 0.1) {
          stemVector.y = 0.15;
        }
      } else {
        // Default: stem points upward
        stemVector = { x: 0, y: stemLength, z: 0 };
      }
      
      console.log('Anchor position (validated):', anchorPosition);
      console.log('Stem vector:', stemVector);

      // Convert color string to RGB object if needed
      let color = pendingTagData.color;
      if (typeof color === 'string') {
        if (color.startsWith('#')) {
          const hex = color.slice(1);
          color = {
            r: parseInt(hex.substr(0, 2), 16) / 255,
            g: parseInt(hex.substr(2, 2), 16) / 255,
            b: parseInt(hex.substr(4, 2), 16) / 255,
          };
        } else {
          color = { r: 0.8, g: 0.6, b: 0.2 }; // Default gold
        }
      }

      // Use NEW mpSdk.Tag.add API instead of deprecated Mattertag.add
      // Note: Tag.add takes a single object, NOT an array like Mattertag.add
      console.log('Calling mpSdk.Tag.add with NEW API...');
      const [tagId] = await sdkInstance.Tag.add({
        label: pendingTagData.label,
        description: pendingTagData.description || '',
        anchorPosition: anchorPosition,
        stemVector: stemVector,
        color: color,
      });

      console.log('Tag created successfully with NEW Tag API! ID:', tagId);

      // Save tag to database for persistence
      const tourId = currentTourIdRef.current;
      console.log('Saving to database - tourId:', tourId, 'API_URL:', API_URL);
      
      if (tourId) {
        try {
          const token = getAuthToken();
          console.log('Got auth token:', token ? 'yes' : 'no');
          
          if (token) {
            const response = await fetch(`${API_URL}/api/tags`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                label: pendingTagData.label,
                description: pendingTagData.description || '',
                tour_id: tourId,
                position: anchorPosition,
                normal: lockedIntersection.normal,
                stem_vector: stemVector,
                color: color,
              })
            });
            
            console.log('Save tag response status:', response.status);
            
            if (response.ok) {
              const savedTag = await response.json();
              console.log('Tag saved to database:', savedTag.id);
              
              // Update the tag with the Matterport ID
              await fetch(`${API_URL}/api/tags/${savedTag.id}/matterport-id?matterport_tag_id=${tagId}`, {
                method: 'PATCH',
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
            } else {
              console.error('Failed to save tag:', await response.text());
            }
          } else {
            console.warn('No auth token available for saving tag');
          }
        } catch (e) {
          console.warn('Could not save tag to database:', e);
        }
      } else {
        console.warn('No tour ID available for saving tag');
      }

      // Add the newly created tag directly to the tags list (don't wait for subscription)
      // This prevents lock-up and ensures immediate UI update
      const newTagObject = {
        id: tagId,
        label: pendingTagData.label,
        description: pendingTagData.description || '',
        position: anchorPosition,
        stemVector: stemVector,
        color: color,
        navigationType: 'tag',
      };
      
      setTags(prevTags => [...prevTags, newTagObject]);
      console.log('Added new tag to state:', newTagObject);

      // Call callback if provided
      if (tagPlacementCallbackRef.current) {
        tagPlacementCallbackRef.current(tagId, anchorPosition);
      }

      // Exit placement mode
      setIsTagPlacementMode(false);
      setPendingTagData(null);
      setLockedIntersection(null);
      tagPlacementCallbackRef.current = null;

      console.log('=== TAG PLACEMENT COMPLETE ===');
      return tagId;
    } catch (e) {
      console.error('Place tag at intersection failed:', e);
      // Try fallback to legacy Mattertag API
      console.log('Attempting fallback to legacy Mattertag API...');
      try {
        const stemVector = {
          x: (lockedIntersection.normal?.x || 0) * 0.15,
          y: Math.max(0.15, (lockedIntersection.normal?.y || 0.3) * 0.15),
          z: (lockedIntersection.normal?.z || 0) * 0.15,
        };
        
        let color = pendingTagData.color;
        if (typeof color === 'string' && color.startsWith('#')) {
          const hex = color.slice(1);
          color = {
            r: parseInt(hex.substr(0, 2), 16) / 255,
            g: parseInt(hex.substr(2, 2), 16) / 255,
            b: parseInt(hex.substr(4, 2), 16) / 255,
          };
        } else if (typeof color === 'string') {
          color = { r: 0.8, g: 0.6, b: 0.2 };
        }
        
        const [tagId] = await sdkInstance.Mattertag.add([{
          label: pendingTagData.label,
          description: pendingTagData.description || '',
          anchorPosition: lockedIntersection.position,
          stemVector: stemVector,
          color: color,
        }]);
        console.log('Tag created via legacy Mattertag API:', tagId);
        
        // Add the newly created tag to the list
        const newTagObject = {
          id: tagId,
          label: pendingTagData.label,
          description: pendingTagData.description || '',
          position: lockedIntersection.position,
          stemVector: stemVector,
          color: color,
          navigationType: 'mattertag',
        };
        setTags(prevTags => [...prevTags, newTagObject]);
        
        // Exit placement mode
        setIsTagPlacementMode(false);
        setPendingTagData(null);
        setLockedIntersection(null);
        
        return tagId;
      } catch (e2) {
        console.error('Legacy Mattertag fallback also failed:', e2);
        // Still exit placement mode on error
        setIsTagPlacementMode(false);
        setPendingTagData(null);
        setLockedIntersection(null);
        return null;
      }
    }
  }, [sdkInstance, lockedIntersection, pendingTagData]);

  // Create tag using NEW Tag API
  const createTag = useCallback(async (tagData) => {
    if (!sdkInstance) {
      const newTag = { id: `custom-${Date.now()}`, ...tagData, isCustom: true };
      setCustomTags(prev => [...prev, newTag]);
      return newTag;
    }
    try {
      // Use NEW Tag.add API (single object, not array)
      const anchorPos = {
        x: Number(tagData.position?.x) || 0,
        y: Number(tagData.position?.y) || 0,
        z: Number(tagData.position?.z) || 0,
      };
      const stemVec = {
        x: Number(tagData.stemVector?.x) || 0,
        y: Number(tagData.stemVector?.y) || 0.15,
        z: Number(tagData.stemVector?.z) || 0,
      };
      
      const [tagId] = await sdkInstance.Tag.add({
        label: tagData.label,
        description: tagData.description || '',
        anchorPosition: anchorPos,
        stemVector: stemVec,
        color: tagData.color || { r: 0, g: 0.5, b: 1 },
      });
      return tagId;
    } catch (e) {
      console.warn('Tag.add failed, trying legacy Mattertag:', e);
      // Fallback to legacy API (still uses array)
      try {
        const [tagId] = await sdkInstance.Mattertag.add([{
          label: tagData.label,
          description: tagData.description || '',
          anchorPosition: tagData.position,
          stemVector: tagData.stemVector || { x: 0, y: 0.15, z: 0 },
          color: tagData.color || { r: 0, g: 0.5, b: 1 },
        }]);
        return tagId;
      } catch (e2) {
        console.error('Create tag failed:', e2);
        return null;
      }
    }
  }, [sdkInstance]);

  // Create a tag with a CUSTOM ICON using HTML billboard
  // This allows animated GIFs, custom PNGs, etc.
  // 
  // USAGE EXAMPLE:
  // createCustomIconTag({
  //   label: 'Fire Extinguisher',
  //   description: 'Class ABC extinguisher - Last inspected: Jan 2025',
  //   position: { x: 1.5, y: 1.0, z: -2.3 },
  //   iconUrl: '/icons/fire-extinguisher.png',  // Custom icon
  //   animated: true,  // Optional pulse animation
  // });
  //
  const createCustomIconTag = useCallback(async (tagData) => {
    if (!sdkInstance) return null;
    
    try {
      console.log('Creating custom icon tag:', tagData);
      
      // Validate position
      const anchorPosition = {
        x: Number(tagData.position?.x) || 0,
        y: Number(tagData.position?.y) || 0,
        z: Number(tagData.position?.z) || 0,
      };
      
      const stemVector = tagData.stemVector || { x: 0, y: 0.2, z: 0 };
      
      // First create a basic tag - this provides the click target and popup
      const [tagId] = await sdkInstance.Tag.add({
        label: tagData.label || '',
        description: tagData.description || '',
        anchorPosition: anchorPosition,
        stemVector: stemVector,
        color: tagData.color || { r: 0.77, g: 0.64, b: 0.29 }, // Zentara gold
      });
      
      console.log('Base tag created:', tagId);

      // If custom icon URL provided, try to attach HTML billboard
      if (tagData.iconUrl && sdkInstance.Tag.registerSandbox) {
        try {
          // Create custom HTML content with the icon
          const htmlContent = `
            <div style="
              width: 64px;
              height: 64px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
            ">
              <img 
                src="${tagData.iconUrl}" 
                alt="${tagData.label || 'Info Hotspot'}"
                style="
                  width: 48px;
                  height: 48px;
                  filter: drop-shadow(0 2px 8px rgba(0,0,0,0.5));
                  ${tagData.animated ? 'animation: pulse 2s infinite;' : ''}
                "
                onerror="this.style.display='none'"
              />
            </div>
            ${tagData.animated ? `
              <style>
                @keyframes pulse {
                  0%, 100% { transform: scale(1); opacity: 1; }
                  50% { transform: scale(1.15); opacity: 0.95; }
                }
              </style>
            ` : ''}
          `;
          
          await sdkInstance.Tag.registerSandbox(tagId, htmlContent);
          console.log('Custom icon billboard attached to tag:', tagId);
        } catch (htmlError) {
          console.warn('HTML billboard not supported in this SDK version, using standard tag:', htmlError.message);
        }
      }

      // Save to database if we have a tour ID
      const tourId = currentTourIdRef.current;
      if (tourId) {
        const token = getAuthToken();
        if (token) {
          try {
            const response = await fetch(`${API_URL}/api/tags`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                label: tagData.label || '',
                description: tagData.description || '',
                tour_id: tourId,
                position: anchorPosition,
                stem_vector: stemVector,
                color: tagData.color || { r: 0.77, g: 0.64, b: 0.29 },
                icon_url: tagData.iconUrl || null,
                is_info_hotspot: true,
              })
            });
            
            if (response.ok) {
              const savedTag = await response.json();
              console.log('Info hotspot saved to database:', savedTag.id);
              
              // Update matterport_tag_id
              await fetch(`${API_URL}/api/tags/${savedTag.id}/matterport-id?matterport_tag_id=${tagId}`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
              });
            }
          } catch (dbError) {
            console.warn('Could not save info hotspot to database:', dbError);
          }
        }
      }

      // Add to local tags state
      const newTagObject = {
        id: tagId,
        label: tagData.label || '',
        description: tagData.description || '',
        position: anchorPosition,
        stemVector: stemVector,
        color: tagData.color,
        iconUrl: tagData.iconUrl,
        navigationType: 'tag',
        isInfoHotspot: true,
      };
      setTags(prevTags => [...prevTags, newTagObject]);

      return tagId;
    } catch (e) {
      console.error('Create custom icon tag failed:', e);
      return null;
    }
  }, [sdkInstance]);

  // Create an INFO HOTSPOT tag with emoji icon using HTML billboard
  const createInfoHotspotTag = useCallback(async (tagData) => {
    if (!sdkInstance) return null;
    
    try {
      console.log('Creating info hotspot tag with emoji:', tagData);
      
      const anchorPosition = {
        x: Number(tagData.position?.x) || 0,
        y: Number(tagData.position?.y) || 0,
        z: Number(tagData.position?.z) || 0,
      };
      
      const stemVector = tagData.stemVector || { x: 0, y: 0.15, z: 0 };
      const emoji = tagData.emoji || '📍';
      const color = tagData.color || { r: 0.77, g: 0.64, b: 0.29 };
      
      // Create the tag with emoji in the label
      const [tagId] = await sdkInstance.Tag.add({
        label: `${emoji} ${tagData.label || ''}`,
        description: '', // Keep empty - we use custom dialog
        anchorPosition: anchorPosition,
        stemVector: stemVector,
        color: color,
      });
      
      console.log('Info hotspot tag created:', tagId);
      
      // Disable default navigation - we'll handle it ourselves with saved rotation
      try {
        await sdkInstance.Tag.allowAction(tagId, {
          navigating: false,  // Prevent auto-navigation on click
          opening: false      // Prevent billboard - we show our custom dialog
        });
        console.log('Disabled default navigation for info hotspot tag:', tagId);
      } catch (allowErr) {
        console.warn('Could not disable tag navigation:', allowErr.message);
      }
      
      // Try to attach custom HTML billboard with larger emoji
      if (sdkInstance.Tag.registerSandbox) {
        try {
          const bgColor = `rgba(${Math.round(color.r * 255)}, ${Math.round(color.g * 255)}, ${Math.round(color.b * 255)}, 0.9)`;
          const htmlContent = `
            <div style="
              width: 48px;
              height: 48px;
              border-radius: 50%;
              background: ${bgColor};
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              box-shadow: 0 2px 8px rgba(0,0,0,0.3);
              border: 2px solid white;
              animation: pulse 2s infinite;
            ">
              <span style="font-size: 24px; line-height: 1;">${emoji}</span>
            </div>
            <style>
              @keyframes pulse {
                0%, 100% { transform: scale(1); box-shadow: 0 2px 8px rgba(0,0,0,0.3); }
                50% { transform: scale(1.1); box-shadow: 0 4px 16px rgba(0,0,0,0.4); }
              }
            </style>
          `;
          
          await sdkInstance.Tag.registerSandbox(tagId, htmlContent);
          console.log('Emoji billboard attached to info hotspot tag:', tagId);
        } catch (htmlError) {
          console.warn('HTML billboard not supported, using text label:', htmlError.message);
        }
      }
      
      return tagId;
    } catch (e) {
      console.error('Create info hotspot tag failed:', e);
      return null;
    }
  }, [sdkInstance]);

  // Delete tag using NEW Tag API
  // tagId can be either a Matterport SDK ID or our database ID
  // tag objects have: id (SDK ID), dbId (our DB ID), isFromDatabase (boolean)
  const deleteTag = useCallback(async (tagId, dbId = null) => {
    console.log('Attempting to delete tag:', tagId, 'dbId:', dbId);
    const tourId = currentTourIdRef.current;
    const token = getAuthToken();
    
    if (tagId.startsWith('custom-')) {
      setCustomTags(prev => prev.filter(t => t.id !== tagId));
      console.log('Custom tag deleted from state');
      return true;
    }
    
    // First, remove from UI state immediately for responsive UX
    setTags(prev => prev.filter(t => t.id !== tagId));
    
    // Try to remove from SDK (for session)
    if (sdkInstance) {
      try {
        console.log('Trying Tag.remove API...');
        await sdkInstance.Tag.remove(tagId);
        console.log('Tag removed from SDK session via Tag.remove');
      } catch (e) {
        console.warn('Tag.remove failed, trying legacy Mattertag:', e.message);
        try {
          await sdkInstance.Mattertag.remove(tagId);
          console.log('Tag removed from SDK session via Mattertag.remove');
        } catch (e2) {
          console.warn('SDK deletion failed (expected for native Matterport tags):', e2.message);
        }
      }
    }
    
    // If we have a database ID, delete from our database
    if (token && dbId) {
      try {
        const response = await fetch(`${API_URL}/api/tags/${dbId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          console.log('Tag deleted from our database:', dbId);
          return true;
        } else {
          console.warn('Failed to delete tag from database:', response.status);
        }
      } catch (e) {
        console.warn('Failed to delete tag from database:', e);
      }
    }
    
    // Check if this is a tag from our database (has our UUID format) - fallback check
    const isOurDatabaseTag = tagId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
    
    if (token && isOurDatabaseTag && !dbId) {
      // This is a tag ID that looks like our UUID - delete from our database
      try {
        const response = await fetch(`${API_URL}/api/tags/${tagId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          console.log('Tag deleted from our database (by UUID):', tagId);
          return true;
        }
      } catch (e) {
        console.warn('Failed to delete tag from database:', e);
      }
    } else if (tourId && token && !dbId) {
      // This is a native Matterport tag - we can't delete it, so hide it
      try {
        await fetch(`${API_URL}/api/tags/hidden/${tourId}/${tagId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setHiddenTagIds(prev => [...prev, tagId]);
        console.log('Native Matterport tag hidden in database');
      } catch (e) {
        console.warn('Failed to hide native tag:', e);
      }
    }
    
    return true;
  }, [sdkInstance]);

  const startTour = useCallback(async () => {
    if (sdkInstance) {
      try {
        await sdkInstance.Tour?.start();
      } catch (e) {
        console.warn('Could not start tour:', e.message);
      }
    }
  }, [sdkInstance]);

  const stopTour = useCallback(async () => {
    if (sdkInstance) {
      try {
        await sdkInstance.Tour?.stop();
      } catch (e) {
        console.warn('Could not stop tour:', e.message);
      }
    }
  }, [sdkInstance]);

  const zoomTo = useCallback(async (level) => {
    if (sdkInstance) await sdkInstance.Camera?.zoomTo(level);
  }, [sdkInstance]);

  // Get current zoom level from SDK
  const getZoom = useCallback(async () => {
    if (!sdkInstance) return 1.0;
    try {
      const zoom = await sdkInstance.Camera?.getZoom?.();
      return zoom || 1.0;
    } catch (e) {
      console.warn('Could not get zoom level:', e);
      return 1.0;
    }
  }, [sdkInstance]);

  // Close currently open tag popup
  const closeTag = useCallback(async (tagId = null) => {
    if (!sdkInstance) return;
    const idToClose = tagId || openTagId;
    if (!idToClose) return;
    
    try {
      if (sdkInstance.Tag?.close) {
        await sdkInstance.Tag.close(idToClose);
        console.log('Tag closed:', idToClose);
      }
      setOpenTagId(null);
    } catch (e) {
      console.warn('Could not close tag:', e);
      setOpenTagId(null);
    }
  }, [sdkInstance, openTagId]);

  const setViewMode = useCallback(async (mode) => {
    if (!sdkInstance) {
      console.warn('SDK not connected for mode change');
      return;
    }
    
    try {
      // Check if Mode module is available
      if (!sdkInstance.Mode) {
        console.warn('Mode module not available');
        return;
      }
      
      // Use the Mode.Mode enum constants
      const ModeEnum = sdkInstance.Mode.Mode;
      console.log('Mode.Mode enum:', ModeEnum);
      
      // Map user-friendly names to Mode enum values
      const modeMap = {
        'dollhouse': ModeEnum?.DOLLHOUSE,
        'floorplan': ModeEnum?.FLOORPLAN, 
        'inside': ModeEnum?.INSIDE,
        'outside': ModeEnum?.OUTSIDE,
      };
      
      const targetMode = modeMap[mode];
      console.log('Attempting to switch to mode:', mode, '-> targetMode:', targetMode);
      
      if (targetMode && sdkInstance.Mode.moveTo) {
        // moveTo returns a promise
        const result = await sdkInstance.Mode.moveTo(targetMode).catch(err => {
          console.log('Mode.moveTo caught error:', err);
          return null;
        });
        console.log('Mode switch result:', result);
      } else {
        console.warn('Invalid mode or moveTo not available');
      }
    } catch (e) {
      console.error('Mode change exception:', e);
    }
  }, [sdkInstance]);

  const addQuickNavPoint = useCallback((p) => {
    setQuickNavPoints(prev => [...prev, { id: `nav-${Date.now()}`, ...p }]);
  }, []);

  const removeQuickNavPoint = useCallback((id) => {
    setQuickNavPoints(prev => prev.filter(p => p.id !== id));
  }, []);

  // Convert 3D world coordinates to 2D screen coordinates
  const worldToScreen = useCallback(async (worldPosition) => {
    if (!sdkInstance?.Conversion?.worldToScreen) {
      console.warn('worldToScreen not available');
      return null;
    }
    
    try {
      const screenCoords = await sdkInstance.Conversion.worldToScreen(worldPosition);
      return screenCoords;
    } catch (e) {
      console.warn('worldToScreen error:', e);
      return null;
    }
  }, [sdkInstance]);

  useEffect(() => {
    const currentSubs = subscriptionsRef.current;
    return () => {
      currentSubs.forEach(sub => sub?.cancel?.());
    };
  }, []);

  return {
    iframeRef, sdkInstance, connectionStatus, error, modelData, cameraPosition,
    sweeps, tags, customTags, quickNavPoints, currentSweep, persistedTags, hiddenTagIds,
    pointerIntersection, lockedIntersection, isTagPlacementMode, pendingTagData,
    matterportTourStops, matterportLabels, openTagId,
    connect, getIframeSrc, navigateToSweep, navigateToPosition, getCurrentPose, navigateToTag,
    navigateToTourStop, navigateToModelLocation, navigateToLabel,
    createTag, createCustomIconTag, createInfoHotspotTag, deleteTag, addQuickNavPoint, removeQuickNavPoint,
    startTagPlacement, cancelTagPlacement, placeTagAtIntersection, lockTagPosition,
    startTour, stopTour, zoomTo, getZoom, closeTag, setViewMode, worldToScreen,
  };
}

export default useMatterport;
